import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Box, Text, FormLabel, Input, Flex, Radio, Stack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  SessionType,
  setEditSessionDate,
  setEditSessionStartTime,
  setEditSessionEndTime,
  setEditSessionTitle,
  setEditSessionType,
} from '../../../redux/reducers';
import InfoText from './infoText';
import { DatePicker, TimePicker, SessionDuration } from '../../';
import { calculateDurationSeconds } from '../../../utils/session/session';
import styled from '@emotion/styled';

const FormContainer = styled(Box)`
  & .customDateTimePicker {
    width: 14em;
    height: 2.5em;
    background-color: #ffffff;
  }

  & .customDateTimePickerPadding > div {
    padding: 4px;
  }
`;

const SessionForm = ({ onChanged, editSession }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const todayMoment = moment().startOf('day');

  useEffect(() => {
    const { dateStr, startTimeStr, endTimeStr } = editSession || {};
    const dateValid = isValidDate(dateStr);
    const rangeValid = calculateDurationSeconds(startTimeStr, endTimeStr) != null;

    onChanged({
      valid: dateValid && rangeValid,
      data: editSession,
    });
  }, [editSession]);

  const isValidDate = (dateStr) => {
    if (dateStr == null) {
      return true;
    }
    const date = moment(dateStr, 'YYYY-MM-DD');
    return date.isValid() && date.isBefore(todayMoment) === false;
  };

  const isValidStartTime = (startTimeStr, endTimeStr) => {
    if (startTimeStr == '') {
      return true;
    }
    const startTime = moment(startTimeStr, 'HH:mm');
    const startTimeValid = startTime.isValid();

    let rangeValid = true;
    if (endTimeStr != '' && startTimeValid === true) {
      rangeValid = startTime.isBefore(moment(endTimeStr, 'HH:mm'));
    }

    return startTimeValid && rangeValid;
  };

  const isValidEndTime = (startTimeStr, endTimeStr) => {
    if (endTimeStr == '') {
      return true;
    }

    const endTime = moment(endTimeStr, 'HH:mm');
    const endTimeValid = endTime.isValid();

    let rangeValid = true;
    if (startTimeStr != '' && endTimeValid === true) {
      rangeValid = endTime.isAfter(moment(startTimeStr, 'HH:mm'));
    }

    return endTimeValid && rangeValid;
  };

  const session = editSession || {};
  return (
    <FormContainer>
      <Text>{t('interpreter.form.mandatoryFieldInfo')}</Text>
      <FormLabel mt={4}>{t('interpreter.form.title')}</FormLabel>
      <Input
        bg="brand.10"
        borderRadius={0}
        value={session.title}
        onChange={(e) => {
          dispatch(setEditSessionTitle(e.target.value));
        }}
        placeholder={t('interpreter.form.placeholderTitle')}
      />
      <InfoText mt={4} text={t('interpreter.form.titleInfo')} />
      <FormLabel mt={4}>{`${t('interpreter.form.sessionType')} *`}</FormLabel>
      <Stack
        spacing={4}
        direction="row"
        value={session.type || SessionType.TEXT}
        onChange={(event) => {
          dispatch(setEditSessionType(event.target.value));
        }}
      >
        <Radio
          colorScheme="gray"
          value={SessionType.TEXT}
          isChecked={session.type === SessionType.TEXT}
        >
          {t('interpreter.form.textInterpretation')}
        </Radio>
        <Radio
          colorScheme="gray"
          value={SessionType.VIDEO}
          isChecked={session.type === SessionType.VIDEO}
        >
          {t('interpreter.form.signInterpretation')}
        </Radio>
      </Stack>
      <InfoText mt={4} text={t('interpreter.form.sessionInfo')} />
      <FormLabel mt={4}>{`${t('interpreter.form.date')} *`}</FormLabel>
      <DatePicker
        isValid={isValidDate(session.dateStr)}
        dateFormat="dd.MM.yyyy"
        placeholderText={t('common.datePlaceholder')}
        minDate={todayMoment.toDate()}
        selected={session.dateStr != null ? moment(session.dateStr, 'YYYY-MM-DD').toDate() : null}
        onChange={(date) => {
          console.log(date);
          dispatch(setEditSessionDate(date !== null ? moment(date).format('YYYY-MM-DD') : null));
        }}
      />
      <Flex mt={4} align="center" justify="space-between" wrap="wrap">
        <Box>
          <FormLabel>{`${t('interpreter.form.startTime')} *`}</FormLabel>
          <TimePicker
            isValid={isValidStartTime(session.startTimeStr, session.endTimeStr)}
            format="HH:mm"
            value={session.startTimeStr}
            required={true}
            onChange={(time) => {
              dispatch(setEditSessionStartTime(time.target.value));
            }}
          />
        </Box>
        <Box>
          <FormLabel>{`${t('interpreter.form.stopTime')} *`}</FormLabel>
          <TimePicker
            isValid={isValidEndTime(session.startTimeStr, session.endTimeStr)}
            format="HH:mm"
            value={session.endTimeStr}
            required={true}
            onChange={(time) => {
              dispatch(setEditSessionEndTime(time.target.value));
            }}
          />
        </Box>
      </Flex>
      <Flex mt={4} alignItems="center">
        <Text mr={1}>{`${t('interpreter.form.sessionEstimatedDuration')}:`}</Text>
        <SessionDuration fontWeight="bold" durationSeconds={session.estimatedDurationSeconds} />
      </Flex>
    </FormContainer>
  );
};

SessionForm.propTypes = {
  onChanged: PropTypes.func.isRequired,
  editSession: PropTypes.object.isRequired,
};

export default SessionForm;
